if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.submitButton = this.querySelector('[type="submit"]');
        this.hideErrors = this.dataset.hideErrors === 'true';
        this.isGiftCard = this.dataset.giftCard === 'true';

        // Listen for variant changes to reset error state
        this.variantIdInput.addEventListener('change', this.onVariantChange.bind(this));

        // Set up gift card email validation
        if (this.isGiftCard) {
          // The recipient form may be outside the product-form element but linked via form attribute
          // Look within the parent product-info element, or fall back to document
          const productInfo = this.closest('product-info') || document;
          this.recipientEmailInput = productInfo.querySelector('[data-gift-card-recipient-email]');
          this.recipientEmailError = productInfo.querySelector('[data-gift-card-recipient-email-error]');

          if (this.recipientEmailInput) {
            this.recipientEmailInput.addEventListener('input', () => {
              this.clearRecipientEmailError();
              this.updateGiftCardButtonState();
            });
          }
        }
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.disabled) return;

        // Validate gift card recipient email if this is a gift card
        if (this.isGiftCard && !this.validateRecipientEmail()) {
          return;
        }

        this.handleErrorMessage();
        this.setLoadingState(true);

        const formData = new FormData(this.form);

        // For gift cards, manually add recipient form fields that are outside the form
        if (this.isGiftCard) {
          const productInfo = this.closest('product-info');
          const recipientForm = productInfo?.querySelector('[data-gift-card-recipient-form]');
          console.log('Gift card form submission:', {
            isGiftCard: this.isGiftCard,
            productInfo: !!productInfo,
            recipientForm: !!recipientForm,
          });
          if (recipientForm) {
            const inputs = recipientForm.querySelectorAll('input, textarea');
            console.log('Found inputs:', inputs.length);
            inputs.forEach((input) => {
              console.log('Input:', input.name, '=', input.value);
              if (input.name) {
                formData.append(input.name, input.value);
              }
            });
          }
        }

        // Debug: log all formData entries
        console.log('FormData entries:');
        for (const [key, value] of formData.entries()) {
          console.log(`  ${key}: ${value}`);
        }

        const config = {
          method: 'POST',
          headers: { 'X-Requested-With': 'XMLHttpRequest' },
          body: formData,
        };

        fetch(`${routes.cart_add_url}.js`, config)
          .then((response) => {
            return response.json().then((data) => {
              return { ok: response.ok, status: response.status, data };
            });
          })
          .then(({ ok, status, data }) => {
            // Check if response was successful
            if (!ok || status !== 200) {
              // Shopify returns errors in different formats
              let errorMessage = data.description || data.message || 'Unable to add item to cart.';

              // Always update cart count for any error because Shopify often adds available quantity
              // even when returning an error (e.g., when requesting 10 but only 5 available)
              this.updateCartCount();

              // Handle specific inventory errors with user-friendly message
              if (
                errorMessage.toLowerCase().includes('inventory') ||
                errorMessage.toLowerCase().includes('available') ||
                errorMessage.toLowerCase().includes('stock') ||
                errorMessage.toLowerCase().includes('quantity')
              ) {
                // More user-friendly inventory message
                errorMessage =
                  "Sorry, there aren't enough items in stock. We've added what's available to your basket.";
              }

              this.handleErrorMessage(errorMessage);
              this.error = true;

              return;
            }

            // Success - response should be the cart item data
            this.updateCartCount();
            this.error = false;

            // Trigger Klaviyo Added to Cart event
            this.triggerKlaviyoAddedToCart(data);

            // Fetch current cart state and dispatch event for cart drawer
            fetch('/cart.js')
              .then((response) => response.json())
              .then((cartData) => {
                document.dispatchEvent(
                  new CustomEvent('cart:updated', {
                    detail: cartData,
                  })
                );
              })
              .catch((error) => console.error('Error fetching cart data:', error));
          })
          .catch((error) => {
            console.error('Cart add error:', error);
            this.handleErrorMessage('Something went wrong. Please try again.');
            this.error = true;
          })
          .finally(() => {
            this.setLoadingState(false);
          });
      }

      setLoadingState(loading) {
        const sectionId = this.dataset.sectionId;
        const submitText = document.getElementById(`submit-button-text-${sectionId}`);
        const loadingText = document.getElementById(`submit-button-loading-${sectionId}`);

        if (loading) {
          this.submitButton.disabled = true;
          this.submitButton.setAttribute('aria-disabled', 'true');
          this.submitButton.classList.add('loading');
          if (submitText) submitText.classList.add('hidden');
          if (loadingText) loadingText.classList.remove('hidden');
        } else {
          this.submitButton.classList.remove('loading');
          if (!this.error) {
            this.submitButton.disabled = false;
            this.submitButton.removeAttribute('aria-disabled');
          }
          if (submitText) submitText.classList.remove('hidden');
          if (loadingText) loadingText.classList.add('hidden');
        }
      }

      updateCartCount() {
        fetch('/cart.js')
          .then((response) => response.json())
          .then((cartData) => {
            const cartIcon = document.getElementById('header-basket');
            if (!cartIcon) return;

            // Update cart count display
            const countSpan = cartIcon.querySelector('span.inline-block span[aria-hidden="true"]');
            if (countSpan) {
              const newCount = cartData.item_count < 100 ? cartData.item_count : '99+';
              countSpan.textContent = newCount;
            }

            // Update screen reader text
            const srSpan = cartIcon.querySelector('span.inline-block span.sr-only');
            if (srSpan) {
              srSpan.textContent = `${cartData.item_count} items in cart`;
            }

            // Trigger flash animation
            this.flashCartIcon(cartIcon);
          })
          .catch((error) => console.error('Error updating cart count:', error));
      }

      flashCartIcon(cartIcon) {
        // Remove any existing animation class
        cartIcon.classList.remove('animate-cart-flash');

        // Force reflow to ensure the class removal takes effect
        cartIcon.offsetHeight;

        // Add the animation class
        cartIcon.classList.add('animate-cart-flash');

        // Remove the class after animation completes
        setTimeout(() => {
          cartIcon.classList.remove('animate-cart-flash');
        }, 1200); // Match the animation duration
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        const sectionId = this.dataset.sectionId;
        const errorWrapper = document.getElementById(`product-form-error-wrapper-${sectionId}`);
        if (!errorWrapper) return;

        const errorElement = document.getElementById(`product-form-error-message-${sectionId}`);
        errorWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage && errorElement) {
          errorElement.textContent = errorMessage;
        }
      }

      onVariantChange() {
        // Only clear error messages, don't touch button state
        // Button state is managed by product-info.js via updateProductForm()
        this.handleErrorMessage();
        this.error = false;
      }

      resetErrorState() {
        // Clear any existing error messages
        this.handleErrorMessage();

        // Reset error flag
        this.error = false;

        // Simple direct state management like the quick-add implementation
        this.submitButton.disabled = false;
        this.submitButton.removeAttribute('aria-disabled');
      }

      toggleSubmitButton(disable = true, text) {
        const sectionId = this.dataset.sectionId;
        const submitText = document.getElementById(`submit-button-text-${sectionId}`);

        // Simple direct state management like the quick-add implementation
        this.submitButton.disabled = disable;

        // Only use aria-disabled for accessibility, not for state management
        if (disable) {
          this.submitButton.setAttribute('aria-disabled', 'true');
        } else {
          this.submitButton.removeAttribute('aria-disabled');
        }

        if (text && submitText) {
          submitText.textContent = text;
        }
      }

      triggerKlaviyoAddedToCart(cartItem) {
        // Check if Klaviyo is available
        if (typeof window._learnq !== 'undefined') {
          try {
            // Get product information for Klaviyo event
            const variantId = cartItem.variant_id || cartItem.id;
            const productHandle = cartItem.handle;
            const variantTitle = cartItem.variant_title;
            const productTitle = cartItem.product_title || cartItem.title;
            const price = cartItem.price ? cartItem.price / 100 : 0;
            const quantity = cartItem.quantity || 1;

            // Build the event data
            const eventData = {
              $event_id: `added_to_cart_${variantId}_${Date.now()}`,
              $value: price * quantity,
              AddedItemProductName: productTitle,
              AddedItemProductID: cartItem.product_id,
              AddedItemSKU: cartItem.sku || '',
              AddedItemCategories: [],
              AddedItemImageURL: cartItem.image || '',
              AddedItemURL: `${window.location.origin}/products/${productHandle}`,
              AddedItemPrice: price,
              AddedItemQuantity: quantity,
              ItemNames: [productTitle],
              CheckoutURL: `${window.location.origin}/cart`,
              Items: [
                {
                  ProductID: cartItem.product_id,
                  SKU: cartItem.sku || '',
                  ProductName: productTitle,
                  Quantity: quantity,
                  ItemPrice: price,
                  RowTotal: price * quantity,
                  ProductURL: `${window.location.origin}/products/${productHandle}`,
                  ImageURL: cartItem.image || '',
                  ProductCategories: [],
                },
              ],
            };

            // Add variant title if available
            if (variantTitle && variantTitle !== 'Default Title') {
              eventData['AddedItemVariantTitle'] = variantTitle;
              eventData['Items'][0]['ProductVariant'] = variantTitle;
            }

            // Track the event
            window._learnq.push(['track', 'Added to Cart', eventData]);
          } catch (error) {
            console.error('Error triggering Klaviyo Added to Cart event:', error);
          }
        } else {
          console.warn('Klaviyo (_learnq) not available for tracking Added to Cart event');
        }
      }

      validateRecipientEmail() {
        if (!this.recipientEmailInput) return true;

        const email = this.recipientEmailInput.value.trim();
        const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

        if (!email || !emailPattern.test(email)) {
          this.showRecipientEmailError();
          this.recipientEmailInput.focus();
          return false;
        }

        this.clearRecipientEmailError();
        return true;
      }

      showRecipientEmailError() {
        if (this.recipientEmailInput) {
          this.recipientEmailInput.setAttribute('aria-invalid', 'true');
          this.recipientEmailInput.classList.add('border-red-600');
        }
        if (this.recipientEmailError) {
          this.recipientEmailError.classList.remove('hidden');
        }
      }

      clearRecipientEmailError() {
        if (this.recipientEmailInput) {
          this.recipientEmailInput.removeAttribute('aria-invalid');
          this.recipientEmailInput.classList.remove('border-red-600');
        }
        if (this.recipientEmailError) {
          this.recipientEmailError.classList.add('hidden');
        }
      }

      updateGiftCardButtonState() {
        if (!this.recipientEmailInput) return;

        const email = this.recipientEmailInput.value.trim();
        const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        const isValid = email && emailPattern.test(email);

        if (isValid) {
          this.submitButton.classList.remove('bg-inactive/20');
        } else {
          this.submitButton.classList.add('bg-inactive/20');
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
